import { t } from "i18next";
import { useAuth } from "react-oidc-context";
import store from "../store/store.ts";
import { getBaseUrl } from "../utils/utils";

// For demo purposes I'm using this awesome Star Wars API

// Custom API error to throw
class ApiError extends Error {
  constructor(message, data, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;

  try {
    if (data !== null && typeof data === 'object') {
        this.response = data;
    } else {
        this.response = JSON.parse(data);
    }
    } catch {
      this.response = data;
  }
  }

  toString() {
    return `${this.message}\nResponse:\n${JSON.stringify(this.response, null, 2)}`;
  }
}

const getAuthorizationHeaders = () => {
  const state = store.getState()
  return {
    Authorization: `Bearer ${state.app.token}`,
  }
}

// API wrapper function
const fetchResource = (path, userOptions = {}) => {
  // Define default options
  const defaultOptions = {};
  // Define default headers
  const defaultHeaders = {};

  const options = {
    // Merge options
    ...defaultOptions,
    ...userOptions,
    // Merge headers
    headers: {
      ...defaultHeaders,
      ...userOptions.headers,
      ...getAuthorizationHeaders(),
      "X-Client-Id": "X2B Static Web",
    },
  };


  const basePath = import.meta.env.VITE_API_HOST ? import.meta.env.VITE_API_HOST : (getBaseUrl() + "/api")

  // Build Url
  const url = `${userOptions.excludeBasePath ? "" : basePath}${path}`;
  // console.log(url)

  // Detect is we are uploading a file
  const isFile = options.body instanceof FormData;

  // Stringify JSON data
  // If body is not a file
  if (options.body && typeof options.body === 'object' && !isFile) {
    options.body = JSON.stringify(options.body);
  }

  // Variable which will be used for storing response
  let response = null;

  return fetch(url, options)
    .then(responseObject => {
      // Saving response for later use in lower scopes
      response = responseObject;
      if (response.redirected == true) {
        throw new Error('Not logged in! Redirect');
      }
      // HTTP unauthorized
      if (response.status === 401) {
        // Handle unauthorized requests
        // Maybe redirect to login page?
      }

      // Check for error HTTP error codes
      if (response.status < 200 || response.status >= 300) {
        // Get response as text
        return response.text();
      }

      // Get response as json
      if (response.headers.get("Content-Type") == "text/plain; charset=utf-8") {
        return response.text();
      }

      return response.json();

    })
    // "parsedResponse" will be either text or javascript object depending if
    // "response.text()" or "response.json()" got called in the upper scope
    .then(parsedResponse => {
      // Check for HTTP error codes
      if (response.status < 200 || response.status >= 300) {
        // Throw error
        throw parsedResponse;
      }

      // Request succeeded
      return parsedResponse;
    })
    .catch(error => {
      // Throw custom API error
      // If response exists it means HTTP error occured
      if (response?.status >= 200 && response?.status < 300) {
        console.log(response)
        return { success: true }
      }
      if (response) {
        if (response.status == 403) {
          sessionStorage.removeItem("isloggedIn")
          sessionStorage.removeItem("user")
        }
        throw new ApiError(`${t("message.apiError", { statusCode: response.status })}`, error, response.status);
      } else {
        throw new ApiError(error instanceof Error ? error.message : String(error), null, 'REQUEST_FAILED');
      }
    });
};

const fetchBlobResource = (url, userOptions = {}, onProgress = null) => {
  const options = {
    cache: 'no-store', // prevent ERR_CACHE_WRITE_FAILURE on large files
    ...userOptions,
    headers: {
      ...userOptions.headers,
      ...getAuthorizationHeaders(),
    },
  };

  return fetch(url, options).then(async response => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (!onProgress) return response.blob();

    // Stream response to track byte-level download progress
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : null;
    if (!total) return response.blob();

    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress(Math.round((received / total) * 100));
    }
    return new Blob(chunks);
  });
};

const downloadBlobResource = async (url, fileName, userOptions = {}, onProgress = null) => {
  const blob = await fetchBlobResource(url, userOptions, onProgress);
  const blobUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(blobUrl);
};

const getRedirectedUrl = async (url, options = {}, requireRedirect = false) => {
  const controller = new AbortController();
  const response = await fetch(url, {
    ...options,
    cache: 'no-store',
    redirect: 'follow',
    signal: controller.signal,
  });
  const resolvedUrl = response.url;
  const redirected = response.redirected;
  controller.abort();

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  if (requireRedirect && !redirected) {
    throw new Error('The token request did not redirect to a download path.');
  }

  return resolvedUrl;
};

const downloadBlobResourceViaToken = async (url, fileName, userOptions = {}, onProgress = null) => {
  const tokenUrl = new URL(url, window.location.href);
  tokenUrl.searchParams.set('action', 'download');

  const downloadPath = await getRedirectedUrl(tokenUrl.toString(), {
    headers: {
      ...userOptions.headers,
      ...getAuthorizationHeaders(),
    },
  }, true);

  const downloadActionUrl = new URL(downloadPath);
  downloadActionUrl.searchParams.set('action', 'download');
  const downloadableUrl = await getRedirectedUrl(downloadActionUrl.toString());

  const anchor = document.createElement('a');
  anchor.href = downloadableUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  onProgress?.(100);
};

const fetchGeoserverResource = (path, options = {}) => {
  const basePath = (import.meta.env.GEOSERVER_HOST ? import.meta.env.GEOSERVER_HOST : getBaseUrl()) + "/geoserver"
  const url = `${basePath}${path}`
  return fetch(url, options)
    .then(response => {
      if (response.status < 200 || response.status >= 300) return response.text()
      if (response.headers.get("Content-Type") == "text/plain; charset=utf-8") return response.text()
      return response.json()
    })
    .catch(error => console.error(error))
}
const fetchTrackConvertorResource = (path, options = {}) => {
  const state = store.getState()
  options.headers = {
    ...options.headers,
    authorization: `Bearer ${state.app.token}`,
  };
  const basePath = (import.meta.env.GEOSERVER_HOST ? import.meta.env.GEOSERVER_HOST : getBaseUrl()) + "/converter"
  const url = `${basePath}${path}`
  return fetch(url, options)
    .then(response => {
      if (response.status < 200 || response.status >= 300) return response.text()
      if (response.headers.get("Content-Type") == "text/plain; charset=utf-8") return response.text()
      return response.json()
    })
    .catch(error => console.error(error))
}

const fetchSensorIntersects = (x, y, activeProcesses) => {
  return fetchResource(`/sensors/intersects`, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
    },
    body: {
      x: x,
      y: y,
      activeProcesses,
    }
  })
}

export {
  fetchResource,
  fetchBlobResource,
  downloadBlobResource,
  downloadBlobResourceViaToken,
  fetchGeoserverResource,
  fetchTrackConvertorResource,
  fetchSensorIntersects,
};